import { Check, Factory, ShoppingCart } from 'lucide-react';

import { cn } from '@accio3d/design-system/utils';

import { OrgAudience, type AllowedOrgType } from '../guards/permissions';

const ROLE_OPTIONS = [
  {
    role: OrgAudience.PURCHASER,
    title: 'Purchaser',
    tagline: 'Seek, purchase, and consume manufacturing and engineering services.',
    icon: ShoppingCart,
  },
  {
    role: OrgAudience.SUPPLIER,
    title: 'Supplier',
    tagline: 'Provide manufacturing and engineering services.',
    icon: Factory,
  },
] as const;

interface OrganizationTypePanelsProps {
  value: AllowedOrgType;
  onChange: (value: AllowedOrgType) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Single-choice panels for organization type. An organization is either a
 * purchaser or a supplier — exactly one panel is always selected.
 */
export function OrganizationTypePanels({
  value,
  onChange,
  disabled = false,
  className,
}: OrganizationTypePanelsProps) {
  const select = (role: AllowedOrgType) => {
    if (disabled || role === value) return;
    onChange(role);
  };

  return (
    <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)} role="radiogroup">
      {ROLE_OPTIONS.map(({ role, title, tagline, icon: Icon }) => {
        const isSelected = role === value;
        return (
          <button
            key={role}
            type="button"
            role="radio"
            disabled={disabled}
            aria-checked={isSelected}
            onClick={() => select(role)}
            className={cn(
              'relative flex h-full flex-col items-start gap-3 rounded-xl border p-4 text-left transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isSelected
                ? 'border-primary bg-primary/5 shadow-sm'
                : 'border-border bg-background hover:border-primary/40 hover:bg-muted/40',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <span
              className={cn(
                'absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border transition-colors',
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/30 bg-background text-transparent',
              )}
              aria-hidden
            >
              <Check className="h-3 w-3" />
            </span>

            <span
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-lg',
                isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
              )}
            >
              <Icon className="h-5 w-5" />
            </span>

            <span className="space-y-1 pr-6">
              <span className="block text-base font-semibold text-foreground">{title}</span>
              <span className="block text-sm leading-snug text-muted-foreground">{tagline}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
